import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { tap } from "rxjs/operators";
import { FeedBackForm } from "../components/feedback-box/feedback-box.component";
import { BaseService } from "./base-service";
import { EXPERIENCE_TYPES, EXPERIENCE_TYPE } from "../shared/constants/hosted-tracking-constants";
import { TranslateService } from "@ngx-translate/core";
import { OrderInterface } from "../ts-interfaces/order-interface";
import moment from "moment-timezone";

@Injectable({
  providedIn: "root",
})
export class SharedService extends BaseService {
  public isPreview: boolean = false;
  public fakeSuccess$ = new BehaviorSubject({ message: "success" });
  private isBulkFeedbackSent = new Subject<{ rating: number; comment?: string }>();
  public isBulkFeedbackSent$ = this.isBulkFeedbackSent.asObservable();
  private isFeedbackSent = new Subject<{ rating: number; comment?: string }>();
  public isFeedbackSent$ = this.isFeedbackSent.asObservable();

  constructor(private http: HttpClient, private translate: TranslateService) {
    super();
    this.isPreview = window.location.pathname.includes("preview");
  }

  /**
   * Send bulk feedback for multiple orderIds
   * @param form
   * @param orderIds
   */
  sendBulkFeedback(form: FeedBackForm, orderIds: Array<string>, source: string) {
    if (!this.isPreview) {
      const obj = {
        rating: parseInt(form.rating || ""), // don't really need ' || ""' part. that is just to keep typescript quite
        comment: form.comment,
        source: source,
        orderIds,
      };

      return this.http
        .post(`${this.serverRootPathV2}feedback/bulk`, obj)
        .pipe(tap(() => this.isBulkFeedbackSent.next({ rating: obj.rating, comment: obj.comment })));
    } else {
      return of(this.fakeSuccess$);
    }
  }

  sendFeedback(form: FeedBackForm, orderId: string, source: string) {
    if (!this.isPreview) {
      const obj = {
        rating: parseInt(form.rating || ""), // don't really need ' || ""' part. that is just to keep typescript quite
        comment: form.comment,
        source: source,
      };

      return this.http
        .post(`${this.serverRootPathV2}feedback/${orderId}`, obj)
        .pipe(tap(() => this.isFeedbackSent.next({ rating: obj.rating, comment: obj.comment })));
    } else {
      return of(this.fakeSuccess$);
    }
  }

  cleanPhoneNumber(phone: string): string {
    // Remove spaces, commas, hyphens, and brackets
    return phone.replace(/[\s,\-\(\)]/g, "");
  }

  getTranslatedStatus(experienceType: string, status: string, type: string | null = null) {
    const experienceTypeName = EXPERIENCE_TYPES[experienceType] || "";
    if (experienceTypeName && status) {
      let translationKey = `HOSTED_TRACKING.STATUS_CARD.${status}_${experienceTypeName}`;
      if (type) {
        translationKey = `HOSTED_TRACKING.STATUS_CARD.${status}_${experienceTypeName}_${type}`;
      }
      return this.translate.instant(translationKey) || "";
    } else {
      return "";
    }
  }

  // Generates a display-friendly object for weekday, month, day, and time
  generateTimeToShow(timestamp: number | string, order: OrderInterface) {
    const date = moment(timestamp);
    const match = date
      .tz(order.timeZone)
      .format("ddd, MMM D, YYYY, hh:mm A")
      .match(/^(\w+), (\w+) (\d+), \d+, (\d+:\d+ (?:A|P)M)$/);

    if (!match) return;

    return {
      weekday: this.t(`HOSTED_TRACKING.WEEKDAY_ABBREVIATIONS.${match[1].toUpperCase()}`),
      month: this.t(`HOSTED_TRACKING.MONTH_ABBREVIATIONS.${match[2].toUpperCase()}`),
      day: match[3],
      time: match[4],
    };
  }

  t(key: string): string {
    return this.translate.instant(key);
  }

  getInitialLabels(): {
    afterText: string;
    pickupDate: string;
    pickupBetween: string;
    notApplicable: string;
    atText: string;
    betweenText: string;
    deliveryDateHeading: string;
    arrivingByText: string;
    unavailableText: string;
  } {
    return {
      afterText: this.t("HOSTED_TRACKING.STATUS_CARD.AFTER_TEXT"),
      pickupDate: this.t("HOSTED_TRACKING.STATUS_CARD.PICKUP_DATE"),
      pickupBetween: this.t("HOSTED_TRACKING.STATUS_CARD.PICKUP_BETWEEN"),
      notApplicable: this.t("GENERIC.PLATFORM.NOT_APPLICABLE"),
      atText: this.t("GENERIC.PLATFORM.AT_TEXT").toLowerCase(),
      betweenText: this.t("GENERIC.PLATFORM.BETWEEN_TEXT").toLowerCase(),
      deliveryDateHeading: this.t("HOSTED_TRACKING.STATUS_CARD.DELIVERY_DATE"),
      arrivingByText: this.t("GENERIC.COMMON_PAGES.ARRIVING_BY"),
      unavailableText: this.t("GENERIC.COMMON_PAGES.UNAVAILABLE"),
    };
  }

  isDeliveryExperience(type: string): boolean {
    return type === EXPERIENCE_TYPE.DELIVERY || type === EXPERIENCE_TYPE.SHIPPING;
  }

  isBOPISExperience(type: string): boolean {
    return [
      EXPERIENCE_TYPE.IN_STORE_PICKUP,
      EXPERIENCE_TYPE.CURBSIDE,
      EXPERIENCE_TYPE.SHIP_TO_STORE,
    ].includes(type);
  }
}
